import { Box, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import * as React from "react";
import dayjs from "dayjs";
import AntDatePicker from "../../../Helper/DatePicker/AntDatePicker";
import { getDatePickerDate } from "../../../Helper/DatePicker/DateConverter";
import { rangePresetsForDigitalTwin } from "../../../Static/Data/Analytics/Analytics";
import CustomIcon from "../../../Components/CustomIcon";


const defaultStartDate = getDatePickerDate(undefined, 0)
const defaultEndDate = getDatePickerDate(undefined, 0)

const ChartCalendarV2 = ({
    startDate = defaultStartDate,
    endDate = defaultEndDate,
    onDateChange = () => { },
    dataGapDates = [],
    isDisabled = false,
    disableBeforeDate = '', // YYYY-MM-DD format
    disableAfterDate = '', // YYYY-MM-DD format
    forcePresets = true,
    disablePresets = false, // Set to true to disable presets completely
    maxRangeDays = null, // Maximum number of days allowed in range (e.g., 7)
}) => {

    const [open, setOpen] = React.useState(false);
    const [isCalendarHover, setIsCalendarHover] = React.useState(false);
    const [currentDates, setCurrentDates] = React.useState({
        startDate,
        endDate
    });
    const [rangePresets, setRangePresets] = React.useState([]);
    const [selectedStartDate, setSelectedStartDate] = React.useState(null);

    const handleDateChange = (startDate, endDate) => {
        if (startDate > endDate) {
            return false
        }

        // Validate maxRangeDays if set
        if (maxRangeDays) {
            const start = dayjs(startDate);
            const end = dayjs(endDate);
            const daysDiff = end.diff(start, 'days');

            if (daysDiff > maxRangeDays - 1) {
                // Adjust end date to be within the allowed range
                const adjustedEndDate = start.add(maxRangeDays - 1, 'days').format('YYYY-MM-DD');
                let dateObj = { startDate, endDate: adjustedEndDate }
                setCurrentDates(dateObj)
                onDateChange(dateObj)
                return false
            }
        }

        let dateObj = { startDate, endDate }
        setCurrentDates(dateObj)
        onDateChange(dateObj)
    }

    const handleCalendarOpen = (open) => {
        if (!open) { setIsCalendarHover(false); }
        setOpen(open)
    }

    const updateRangePresets = (updatedValues) => {
        let presetsToUse = updatedValues;

        // Filter presets based on maxRangeDays if set
        if (maxRangeDays) {
            presetsToUse = updatedValues.filter((preset) => {
                if (preset.value && preset.value[0] && preset.value[1]) {
                    const start = dayjs(preset.value[0]);
                    const end = dayjs(preset.value[1]);
                    const daysDiff = end.diff(start, 'day') + 1; // +1 to include both start and end days
                    return daysDiff <= maxRangeDays;
                }
                return true;
            });
        }

        setRangePresets([
            ...presetsToUse,
            {
                label: "Reset",
                value: [dayjs(defaultStartDate), dayjs(defaultEndDate)]
            }
        ]);
    }

    React.useEffect(() => {
        setCurrentDates({ startDate, endDate });
    }, [startDate, endDate]);

    React.useEffect(() => {
        if (disablePresets) {
            // Disable presets completely
            setRangePresets([]);
        } else if (disableBeforeDate) {
            const filteredPresets = rangePresetsForDigitalTwin.filter((range) => {
                const addedOnDate = new Date(disableBeforeDate);
                const startDate = new Date(range.value[0]);
                return addedOnDate <= startDate;
            });
            updateRangePresets(filteredPresets);
        } else if (forcePresets) {
            // Initialize presets when disableBeforeDate is not set
            updateRangePresets(rangePresetsForDigitalTwin);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableBeforeDate, disablePresets, forcePresets, maxRangeDays])

    const borderColor = open && !isDisabled ? "#6D72F6" : "#DEDEDE";
    const labelColor = open && !isDisabled ? "#6D72F6" : "#686868";
    const iconColor = open && !isDisabled ? "#6D72F6" : "#444B51";

    return (
        <Box
            onMouseEnter={() => setIsCalendarHover(true)}
            onMouseLeave={() => setIsCalendarHover(false)}
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor,
                borderRadius: '4px',
                px: 1.5,
                py: 0.75,
                minWidth: 150,
                cursor: isDisabled ? 'default' : 'pointer',
                '&:hover': !isDisabled ? { borderColor: '#6D72F6' } : {},
                // Hide the default Ant Design RangePicker border and background
                '& .ant-picker': {
                    border: 'none !important',
                    boxShadow: 'none !important',
                    background: 'transparent !important',
                    padding: '0 !important',
                    cursor: 'pointer !important',
                },
                '& .ant-picker-focused': {
                    border: 'none !important',
                    boxShadow: 'none !important',
                },
                '& .ant-picker-input > input': {
                    fontSize: '12px !important',
                    color: '#2B2B2B !important',
                    fontWeight: '500 !important',
                    fontFamily: 'inherit !important',
                    cursor: 'pointer !important',
                },
                '& .ant-picker-active-bar': {
                    display: 'none !important',
                },
                '& .ant-picker-suffix': {
                    display: 'none !important',
                },
                // Stretch the picker to cover the full box for click anywhere
                '& .ant-picker-range': {
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                    opacity: 0,
                },
            }}
        >
            <Typography
                variant="fw500s10lh24c5E758D"
                sx={{
                    lineHeight: '12px',
                    color: labelColor,
                    position: 'absolute',
                    top: '-7px',
                    left: '8px',
                    px: 0.5,
                    backgroundColor: '#F9F9F9',
                    zIndex: 2,
                    pointerEvents: 'none',
                }}
            >
                Time Period
            </Typography>
            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
                <CustomIcon stroke={iconColor} name="calendar" />
            </Box>
            {/* Visible date display */}
            <Typography
                variant="fw500s12lh24cA2A2A2"
                sx={{
                    color: '#2B2B2B',
                    lineHeight: '24px',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                }}
            >
                {currentDates.startDate && currentDates.endDate
                    ? `${dayjs(currentDates.startDate).format('DD MMM YYYY')}  -  ${dayjs(currentDates.endDate).format('DD MMM YYYY')}`
                    : ''}
            </Typography>
            {!isDisabled && (
                <ArrowDropDownIcon
                    fontSize="large"
                    sx={{
                        color: open ? "#6D72F6" : "#3D4B5A",
                        transform: open ? "rotate(180deg)" : "",
                        ml: 0.5,
                        pointerEvents: 'none',
                    }}
                />
            )}
            {/* Hidden but functional AntDatePicker stretched over the full box */}
            <AntDatePicker
                isDisabled={isDisabled}
                page={'digitalTwin'}
                popupClassName="analytics-calendar-popup"
                changeTheDate={handleDateChange}
                date={[currentDates.startDate, currentDates.endDate]}
                allowClear={true}
                onOpenChange={handleCalendarOpen}
                dataGapDates={dataGapDates}
                rangePresets={rangePresets}
                onCalendarChange={(dates) => {
                    // Track the first selected date to enable dynamic range limiting
                    if (dates && dates[0] && !dates[1]) {
                        setSelectedStartDate(dates[0]);
                    } else if (!dates || (dates[0] && dates[1])) {
                        setSelectedStartDate(null);
                    }
                }}
                disabledDate={(current) => {
                    // Disable dates before the specified minimum date
                    if (disableBeforeDate && current < dayjs(disableBeforeDate).startOf("day")) {
                        return true;
                    }

                    // Disable dates after the specified maximum date
                    if (disableAfterDate && current > dayjs(disableAfterDate).endOf("day")) {
                        return true;
                    }

                    // If maxRangeDays is set and user has selected a start date, disable dates outside the allowed range
                    if (maxRangeDays && selectedStartDate) {
                        const minSelectableDate = selectedStartDate.subtract(maxRangeDays - 1, 'day');
                        const maxSelectableDate = selectedStartDate.add(maxRangeDays - 1, 'day');

                        // Disable if outside the range
                        if (current < minSelectableDate.startOf('day') || current > maxSelectableDate.endOf('day')) {
                            return true;
                        }
                    }

                    return false;
                }}
            />
        </Box>
    )
}


export default ChartCalendarV2;
